import { TextField, TextFieldProps } from "@mui/material";
import { Serialiser } from "hooks/serialisers/types";
import { ChangeEvent, KeyboardEventHandler, useRef, useState } from "react";

const CURSOR_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

export interface ValidatedTextFieldProps<T, O> {
  value: T;
  setValue: (value: T) => void;
  serialiser: Serialiser<T, O>;
  label?: string;
  disabled?: boolean;
  onEditConfirmedWithKeyboard?: (value: T) => void;
  onEditStartTransform?: (text: string) => string;
  textFieldProps?: TextFieldProps;
  liveUpdate?: boolean;
}

/**
 * Commits only valid field values.
 *
 * Enter, Tab, or blur commits a valid edit and discards an invalid edit.
 * Escape restores the value from the start of the edit.
 */
export const ValidatedTextField = <T, O = undefined>({
  value,
  setValue,
  serialiser,
  label,
  disabled,
  onEditConfirmedWithKeyboard,
  onEditStartTransform,
  textFieldProps,
  liveUpdate,
}: ValidatedTextFieldProps<T, O>) => {
  // Text is only non-`null` while the value is being edited
  const [text, setText] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  const discardingRef = useRef(false);
  const editStartValueRef = useRef(value);

  const handleChange = ({
    target,
  }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const text = target.value;
    if (liveUpdate) {
      const result = serialiser.parse(text);
      if (result.valid) {
        setValue(result.value);
      }
    }
    setText(text);
  };

  const handleBlur = () => {
    if (discardingRef.current) {
      discardingRef.current = false;
      setText(null);
      return;
    }
    if (text !== null) {
      const result = serialiser.parse(text);
      if (result.valid) {
        setValue(result.value);
      }
      setText(null);
    }
  };

  const handleKey: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (text !== null && CURSOR_KEYS.has(event.key)) {
      event.stopPropagation();
    }
    if (event.key === "Escape") {
      // Escape restores the initial value before blur clears the edit state.
      discardingRef.current = true;
      if (liveUpdate) {
        setValue(editStartValueRef.current);
      }
      ref.current?.blur();
      return;
    }
    if (event.key === "Enter") {
      ref.current?.blur();
      const parsedValue = serialiser.parse(text ?? "");
      if (onEditConfirmedWithKeyboard && parsedValue.valid) {
        onEditConfirmedWithKeyboard(parsedValue.value);
      }
    }
  };

  return (
    <TextField
      inputRef={ref}
      fullWidth
      size="small"
      variant="outlined"
      value={text ?? serialiser.format(value)}
      onChange={handleChange}
      onFocus={() => {
        editStartValueRef.current = value;
        // Optionally modify the text (eg. converting "0" to an empty string)
        // when editing starts
        if (onEditStartTransform) {
          setText(onEditStartTransform(text ?? serialiser.format(value)));
        }
      }}
      onBlur={handleBlur}
      onKeyDown={handleKey}
      label={label}
      disabled={disabled === true}
      {...(textFieldProps ?? {})}
    />
  );
};
